import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Estate Management" },
  { to: "/salotto-digitale", label: "Il Salotto Digitale" },
  { to: "/blog", label: "Journal" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const inLearn = location.pathname.startsWith("/learn");

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 border-b transition-all duration-500 ${
        scrolled ? "border-line bg-bg/90 backdrop-blur-xl" : "border-transparent bg-bg/60 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
          <span className="font-serif uppercase text-xl lg:text-2xl font-medium tracking-[0.18em] text-ink leading-none">Marella Bolognesi</span>
          <span className="text-[10px] uppercase tracking-ultra text-ink-soft mt-1">Miami, Florida</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.2em] link-underline transition-colors ${
                  isActive ? "text-ink" : "text-ink-soft hover:text-ink"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          {user ? (
            <Link
              to="/account"
              data-testid="header-account-btn"
              className="text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-ink link-underline"
            >
              My Library
            </Link>
          ) : (
            <Link
              to="/account"
              data-testid="header-login-btn"
              aria-label="Student access"
              title="Student access"
              className="text-ink-soft hover:text-ink transition-colors"
            >
              <User size={18} strokeWidth={1.4} />
            </Link>
          )}
          {!inLearn && (
            <Link
              to="/salotto-digitale"
              data-testid="header-enquire-btn"
              className="bg-ink text-bg px-7 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors"
            >
              Explore Il Salotto
            </Link>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-ink"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={26} strokeWidth={1.3} /> : <Menu size={26} strokeWidth={1.3} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-bg border-t border-line px-6 py-8">
          <nav className="flex flex-col gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-serif text-3xl text-ink"
              >
                {n.label}
              </NavLink>
            ))}
            {user && (
              <Link
                to="/account"
                className="mt-4 border border-ink text-ink px-7 py-4 text-xs uppercase tracking-[0.2em] text-center"
                data-testid="mobile-account-btn"
              >
                My Library
              </Link>
            )}
            {!inLearn && (
              <Link
                to="/salotto-digitale"
                className="bg-ink text-bg px-7 py-4 text-xs uppercase tracking-[0.2em] text-center"
                data-testid="mobile-enquire-btn"
              >
                Explore Il Salotto
              </Link>
            )}
            {!user && (
              <Link
                to="/account"
                className="mt-2 text-[11px] uppercase tracking-[0.2em] text-ink-soft text-center link-underline"
                data-testid="mobile-login-link"
              >
                Student access
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
